import { CPENZLoader } from "@/components/CPENZLoader";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CPENZIcon } from "@/components/CPENZIcon";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import useNoIndex from "@/hooks/useNoIndex";

export function PaymentSuccess() {
  useNoIndex();
  const { t } = useLang();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [projectId, setProjectId] = useState(null);
  const [kind, setKind] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const provider = params.get("provider");
    if (provider === "paypal") {
      const orderId = params.get("token");
      if (!orderId) { setStatus("error"); return; }
      (async () => {
        try {
          const { data } = await api.post(`/payments/paypal/capture/${orderId}`);
          if (data.payment_status === "paid") {
            setProjectId(data.project_id);
            await refresh();
            setStatus("paid");
          } else setStatus("error");
        } catch (e) { setStatus("error"); }
      })();
      return;
    }
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setProjectId(data.project_id);
          setKind(data.kind);
          await refresh();
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(data.status) || attempts.current > 15) {
          setStatus("error"); return;
        }
      } catch (e) {
        if (attempts.current > 15) { setStatus("error"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5 soft-glow">
      <div className="glass-panel rounded-3xl p-10 max-w-md w-full text-center" data-testid="payment-success">
        {status === "checking" && <><CPENZLoader size={96} /><p className="mt-5 text-slate-500">{t("pay.processing")}</p></>}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-green-500" size={56} />
            <h1 className="mt-5 text-2xl font-bold tracking-tight">{kind === "credits" ? t("pay.credits_success") : t("pay.success")}</h1>
            <p className="mt-2 text-slate-500">{kind === "credits" ? t("pay.credits_body") : t("pay.success_body")}</p>
            <Button
              data-testid="payment-continue"
              className="mt-6 rounded-full bg-[#0b0f1a] text-white px-6"
              onClick={() => navigate(kind === "credits" ? "/app/credits" : kind === "market" ? "/app/market/me" : projectId ? `/app/idea/${projectId}` : "/app/dashboard")}
            >
              {t("pay.continue")} →
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto text-red-400" size={56} />
            <h1 className="mt-5 text-2xl font-bold tracking-tight">{t("pay.failed")}</h1>
            <p className="mt-2 text-slate-500">{t("pay.cancel_body")}</p>
            <Button data-testid="payment-retry" className="mt-6 rounded-full" variant="outline" onClick={() => navigate(projectId ? `/app/checkout/${projectId}` : "/app/dashboard")}>{t("pay.try_again")}</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const projectId = params.get("project_id");
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="glass-panel rounded-3xl p-10 max-w-md w-full text-center" data-testid="payment-cancel">
        <XCircle className="mx-auto text-slate-400" size={56} />
        <h1 className="mt-5 text-2xl font-bold tracking-tight">{t("pay.cancel")}</h1>
        <p className="mt-2 text-slate-500">{t("pay.cancel_body")}</p>
        <div className="mt-6 flex gap-3 justify-center">
          <Button className="rounded-full" variant="outline" onClick={() => navigate("/")}>{t("pay.back_home")}</Button>
          <Button data-testid="payment-return-project" className="rounded-full bg-[#0b0f1a] text-white" onClick={() => navigate(projectId ? `/app/idea/${projectId}` : "/app/dashboard")}>{t("pay.return_project")}</Button>
        </div>
      </div>
    </div>
  );
}
